import { logger } from "../../../datadog/logger";
import { noop } from "../../../utils";
import { RemoteParticipant } from "../../core/room/participant/RemoteParticipant";
import { RoomDelegator } from "../../core/room/RoomDelegator";
import { RoomEvent, RoomEventHandlerMap } from "../../core/room/types";
import { VideoClient } from "../types";
import { ZoomEventBus } from "../ZoomEventBus";
import { ZoomLocalParticipant } from "./participant/LocalParticipant";
import { ZoomRemoteParticipant } from "./participant/RemoteParticipant";
import {
  createZoomRoomEventHandler,
  getZoomRoomEvent,
  isEventSupported,
} from "./utils";

export class ZoomRoom implements RoomDelegator {
  #videoClient: VideoClient;
  #sid: string;
  #name: string;

  constructor(videoClient: VideoClient) {
    this.#videoClient = videoClient;
    const { sessionId, topic } = videoClient.getSessionInfo();

    // Zoom updates these the VideoClient object values on room join and leaving
    this.#sid = sessionId;
    this.#name = topic;
  }

  get sid() {
    return this.#sid;
  }

  get name() {
    return this.#name;
  }

  get localParticipant() {
    const currentParticipant = this.#videoClient.getCurrentUserInfo();
    return (
      currentParticipant &&
      new ZoomLocalParticipant(currentParticipant, this.#videoClient)
    );
  }

  // This is used only during joinRoom initialization
  get remoteParticipants() {
    return this.#videoClient
      .getAllUser()
      .reduce((accumulator, remoteParticipant) => {
        if (
          remoteParticipant.userIdentity ===
          this.#videoClient.getCurrentUserInfo()?.userIdentity
        ) {
          // skip the local participant
          return accumulator;
        }

        accumulator.set(
          String(remoteParticipant.userId),
          new ZoomRemoteParticipant(remoteParticipant, this.#videoClient),
        );

        return accumulator;
      }, new Map<string, RemoteParticipant>());
  }

  on<K extends RoomEvent>(event: K, handler: RoomEventHandlerMap[K]) {
    if (!isEventSupported(event as string)) {
      logger.warn(`[ZoomRoom: on] Unsupported event type: ${event}`);
      return noop;
    }

    const zoomEvent = getZoomRoomEvent(event);
    if (!zoomEvent) {
      logger.warn(`[ZoomRoom: on] No Zoom mapping for event: ${event}`);
      return noop;
    }

    const zoomHandler = createZoomRoomEventHandler(
      this.#videoClient,
      event,
      handler,
    );

    const eventBus = ZoomEventBus.getInstance();
    eventBus.on(zoomEvent, zoomHandler);

    return function off() {
      eventBus.off(zoomEvent, zoomHandler);
    };
  }

  async disconnect(): Promise<void> {
    await this.#videoClient.leave();
  }
}
